import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';
import { TokenValidationService } from '../Services/token-validation.service';
import { TokenEncryptionService } from '../Services/token-encryption.service';
import { clearStoragePreservingAttribution } from './attribution.util';

@Injectable({
  providedIn: 'root'
})
export class LoginGuard implements CanActivate {
  /** A locally valid token was confirmed with /users/me this session. */
  private confirmedThisSession = false;

  constructor(
    private router: Router,
    private tokenValidation: TokenValidationService,
    private tokenEncryption: TokenEncryptionService
  ) {
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const token = this.tokenEncryption.getToken();
    const isLoggedIn = localStorage.getItem('isLogedIn') === 'true';

    // If user is not logged in, redirect to login
    if (!token || !isLoggedIn) {
      return this.redirectToLogin(state.url);
    }

    // If token is expired, clear session and redirect
    if (this.tokenValidation.isTokenExpired(token)) {
      this.clearSession();
      return this.redirectToLogin(state.url);
    }

    // A trial company already has /users/me and other calls in flight. Waiting
    // on another /users/me here never returns, so the router keeps the page
    // the user clicked from (Employees, Dashboard) even after the address bar
    // says /billings. A direct /billings load has an empty connection pool, so
    // that path works. Trust a non-expired token and confirm it in the background.
    if (this.confirmedThisSession) {
      return true;
    }
    this.tokenValidation.validateToken(token).pipe(
      timeout(8000),
      take(1),
      catchError(() => of(true))
    ).subscribe((valid) => {
      if (valid) {
        this.confirmedThisSession = true;
        return;
      }
      this.clearSession();
      void this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    });
    return true;
  }

  private redirectToLogin(returnUrl: string): UrlTree {
    return this.router.createUrlTree(['/login'], { queryParams: { returnUrl } });
  }

  private clearSession(): void {
    clearStoragePreservingAttribution(localStorage);
    sessionStorage.clear();
  }
}
